import { Exchange, OptionType } from '@/types/generated/common/types'
import {
  BlockTradeMode,
  type GetOptionsVolumeLeaderboardRequest,
  OptionsOiRankMode,
  OptionsVolumeGroupBy,
  OptionsVolumeMeasure,
} from '@/types/generated/public_api'

// ENG-613 — options analytics shared enums, labels, and exchange-gating helpers.

export const OPTIONS_VOLUME_MEASURE_LABELS: Record<OptionsVolumeMeasure, string> = {
  [OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_UNSPECIFIED]: 'Unspecified',
  [OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_NOTIONAL_BASE]: 'Notional (Base)',
  [OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_NOTIONAL_QUOTE]: 'Notional (Quote)',
  [OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_PREMIUM_BASE]: 'Premium (Base)',
  [OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_PREMIUM_QUOTE]: 'Premium (Quote)',
  [OptionsVolumeMeasure.UNRECOGNIZED]: 'Unrecognized',
}

export const OPTIONS_VOLUME_GROUP_BY_LABELS: Record<OptionsVolumeGroupBy, string> = {
  [OptionsVolumeGroupBy.OPTIONS_VOLUME_GROUP_BY_UNSPECIFIED]: 'Unspecified',
  [OptionsVolumeGroupBy.OPTIONS_VOLUME_GROUP_BY_EXPIRY]: 'Expiry',
  [OptionsVolumeGroupBy.OPTIONS_VOLUME_GROUP_BY_CONTRACT]: 'Contract',
  [OptionsVolumeGroupBy.UNRECOGNIZED]: 'Unrecognized',
}

export const OPTIONS_OI_RANK_MODE_LABELS: Record<OptionsOiRankMode, string> = {
  [OptionsOiRankMode.OPTIONS_OI_RANK_MODE_UNSPECIFIED]: 'Unspecified',
  [OptionsOiRankMode.OPTIONS_OI_RANK_MODE_OI]: 'Open Interest',
  [OptionsOiRankMode.OPTIONS_OI_RANK_MODE_OI_CHANGE_24H]: '24h OI Change',
  [OptionsOiRankMode.UNRECOGNIZED]: 'Unrecognized',
}

export const OPTION_TYPE_LABELS: Record<OptionType, string> = {
  [OptionType.OPTION_TYPE_UNSPECIFIED]: 'All',
  [OptionType.OPTION_TYPE_CALL]: 'Call',
  [OptionType.OPTION_TYPE_PUT]: 'Put',
  [OptionType.UNRECOGNIZED]: 'Unrecognized',
}

export const BLOCK_TRADE_MODE_LABELS: Record<BlockTradeMode, string> = {
  [BlockTradeMode.BLOCK_TRADE_MODE_UNSPECIFIED]: 'All',
  [BlockTradeMode.BLOCK_TRADE_MODE_ALL]: 'All',
  [BlockTradeMode.BLOCK_TRADE_MODE_ONLY]: 'Block only',
  [BlockTradeMode.BLOCK_TRADE_MODE_NONE]: 'Exclude block',
  [BlockTradeMode.UNRECOGNIZED]: 'Unrecognized',
}

/**
 * NOTIONAL_QUOTE / PREMIUM_BASE / PREMIUM_QUOTE are derived from `index_price`,
 * which is populated for Deribit, GLBX, and OPRA. NOTIONAL_BASE works everywhere
 * (e.g. Bybit, which has no index_price).
 */
export const INDEX_PRICE_OPTIONS_VOLUME_MEASURES: OptionsVolumeMeasure[] = [
  OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_NOTIONAL_QUOTE,
  OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_PREMIUM_BASE,
  OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_PREMIUM_QUOTE,
]

export const ALL_OPTIONS_VOLUME_MEASURES: OptionsVolumeMeasure[] = [
  OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_NOTIONAL_BASE,
  OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_NOTIONAL_QUOTE,
  OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_PREMIUM_BASE,
  OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_PREMIUM_QUOTE,
]

const INDEX_PRICE_EXCHANGES: Exchange[] = [
  Exchange.EXCHANGE_DERIBIT,
  Exchange.EXCHANGE_GLBX,
  Exchange.EXCHANGE_OPRA,
]

export function exchangeSupportsIndexPriceMeasures(exchange: Exchange): boolean {
  return INDEX_PRICE_EXCHANGES.includes(exchange)
}

export function isIndexPriceOptionsVolumeMeasure(measure: OptionsVolumeMeasure): boolean {
  return INDEX_PRICE_OPTIONS_VOLUME_MEASURES.includes(measure)
}

/** Measure options to show in a selector for the given exchange (hides index-price measures on venues without index_price, e.g. Bybit). */
export function getAvailableOptionsVolumeMeasures(exchange: Exchange): OptionsVolumeMeasure[] {
  if (exchangeSupportsIndexPriceMeasures(exchange)) return ALL_OPTIONS_VOLUME_MEASURES
  return ALL_OPTIONS_VOLUME_MEASURES.filter((measure) => !isIndexPriceOptionsVolumeMeasure(measure))
}

/**
 * Block-trade filtering is a Deribit-only concept — GLBX/OPRA (Databento) carry
 * no block flag on the wire, so `block_trade_id` is always unset: BLOCK_TRADE_MODE_ONLY
 * returns empty and NONE behaves the same as ALL. Hide the block-mode filter for
 * those exchanges.
 */
export function exchangeSupportsBlockTradeFilter(exchange: Exchange): boolean {
  return exchange === Exchange.EXCHANGE_DERIBIT
}

/**
 * Strike flow splits every bucket into buy/sell by trade aggressor. OPRA's
 * consolidated feed publishes no aggressor side, so every cell would come back
 * as a zeroed buy/sell pair indistinguishable from no trading — the server
 * returns BadRequest rather than serving that. Deribit, Bybit, GLBX and Derive
 * all report side.
 */
export function exchangeSupportsAggressorSide(exchange: Exchange): boolean {
  return exchange !== Exchange.EXCHANGE_OPRA
}

// OI is sampled — sub-minute timeframes are rejected by the server.
export const OPTIONS_OI_SERIES_MIN_TIMEFRAME_SECONDS = 60

// Server-side cap on OI series buckets is 1500; default stays low since the
// chart is a stacked bar (unlike the virtualized leaderboard tables above).
export const OPTIONS_OI_SERIES_DEFAULT_LIMIT = 60
export const OPTIONS_OI_SERIES_MAX_LIMIT = 1500
export const OPTIONS_OI_SERIES_LIMIT_OPTIONS = [60, 100, 250, 500, 1000, 1500]

// Strike flow reads the raw trade tape, so the server caps the effective window
// at min(requested, 90d, (limit + 1) × timeframe). Sub-5m buckets would cover
// well under a day at the default limit, which is too short to read as a
// heatmap — keep the selector at 5m and above.
export const OPTIONS_STRIKE_FLOW_MIN_TIMEFRAME_SECONDS = 300
export const OPTIONS_STRIKE_FLOW_DEFAULT_TIMEFRAME_SECONDS = 3600
// Server defaults; limits are clamped server-side.
export const OPTIONS_STRIKE_FLOW_DEFAULT_LIMIT = 500
export const OPTIONS_STRIKE_FLOW_MAX_LIMIT = 1000
export const OPTIONS_STRIKE_FLOW_DEFAULT_STRIKE_LIMIT = 20
export const OPTIONS_STRIKE_FLOW_MAX_STRIKE_LIMIT = 50

// Leaderboards use a fixed 24h lookback window; limits are clamped server-side.
//
// CONTRACT grouping used to default to 20 while the server allows up to 500 (same
// cap as EXPIRY/UNSPECIFIED) — the table is virtualized (react-virtuoso via
// DataTable), so nothing about rendering needed the row count kept small, and a
// contract-level leaderboard genuinely has far more distinct rows than an
// expiry-level one. Matching the server max here, same as the OI leaderboard below.
export const OPTIONS_VOLUME_LEADERBOARD_DEFAULT_LIMITS: Record<OptionsVolumeGroupBy, number> = {
  [OptionsVolumeGroupBy.OPTIONS_VOLUME_GROUP_BY_UNSPECIFIED]: 100,
  [OptionsVolumeGroupBy.OPTIONS_VOLUME_GROUP_BY_EXPIRY]: 100,
  [OptionsVolumeGroupBy.OPTIONS_VOLUME_GROUP_BY_CONTRACT]: 500,
  [OptionsVolumeGroupBy.UNRECOGNIZED]: 100,
}
export const OPTIONS_VOLUME_LEADERBOARD_MAX_LIMIT = 500

// Was 20 against a server max of 500 — same reasoning as the volume leaderboard's
// CONTRACT default above: the table is virtualized, so there's no rendering reason
// to under-fetch relative to what the server will actually serve.
export const OPTIONS_OI_LEADERBOARD_DEFAULT_LIMIT = 500
export const OPTIONS_OI_LEADERBOARD_MAX_LIMIT = 500

// UNSPECIFIED means "All" (both sides) server-side — the wire field is optional
// and omitting it is how "All" is requested, so UNSPECIFIED must not be sent as-is.
export function normalizeOptionType(optionType: OptionType): OptionType | undefined {
  return optionType === OptionType.OPTION_TYPE_UNSPECIFIED ? undefined : optionType
}

// Extracted so the leaderboard request each groupBy actually gets is directly
// testable — a prior version of the volume leaderboard's call site used the OI
// leaderboard's flat limit constant instead of this per-groupBy map.
export function buildOptionsVolumeLeaderboardRequest(input: {
  exchange: Exchange
  underlying: string
  measure: OptionsVolumeMeasure
  optionType: OptionType
  groupBy: OptionsVolumeGroupBy
}): GetOptionsVolumeLeaderboardRequest {
  return {
    exchange: input.exchange,
    underlying: input.underlying,
    volumeMeasure: input.measure,
    groupBy: input.groupBy,
    optionType: normalizeOptionType(input.optionType),
    limit: OPTIONS_VOLUME_LEADERBOARD_DEFAULT_LIMITS[input.groupBy],
  }
}
